﻿import React, { useState, PropsWithChildren } from "react";
import { IUploadResponse } from "./api";
import {
    guser,
    gfile,
    ggroup,
    gallusrs,
    emptyuser,
    gcompusrs,
    emptygroup,
    gactivecomp,
    gcompmbrshps,
    emptycompany,
    gloggedinuser,
    gselectedgroup,
    permissionlevels,
    emptyloggedinuser,
} from "./interfaces";

// Adjust the context to include filelist, an array of the same type as user
export const GlobalContext = React.createContext<{
    user: guser;
    loggedInUser: gloggedinuser;
    permissionLevels: { [key: number]: string };
    category: string;
    fileList: Array<gfile>;
    groupList: Array<ggroup>;
    selectedGroup: gselectedgroup;
    compUsrs: Array<gcompusrs>;
    allUsrs: Array<gallusrs>;
    compMbrshps: Array<gcompmbrshps>;
    isCalloutVisible: boolean;
    selectedFiles: File[];
    isLoading: boolean;
    uploadedFile: IUploadResponse | undefined;
    isYoutubeCalloutVisible: boolean;
    textYoutubeInput: string;
    isYoutubeLoading: boolean;
    activeComp: gactivecomp;
    currentP: number;
    sourceHighlighting: boolean;
    docAudio: boolean;
    setUser: React.Dispatch<React.SetStateAction<guser>>;
    setLoggedInUser: React.Dispatch<React.SetStateAction<gloggedinuser>>;
    setPermissionLevels: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
    setCategory: React.Dispatch<React.SetStateAction<string>>;
    setFileList: React.Dispatch<React.SetStateAction<Array<gfile>>>;
    setGroupList: React.Dispatch<React.SetStateAction<Array<ggroup>>>;
    setSelectedGroup: React.Dispatch<React.SetStateAction<gselectedgroup>>;
    setCompUsrs: React.Dispatch<React.SetStateAction<Array<gcompusrs>>>;
    setAllUsrs: React.Dispatch<React.SetStateAction<Array<gallusrs>>>;
    setCompMbrshps: React.Dispatch<React.SetStateAction<Array<gcompmbrshps>>>;
    setIsCalloutVisible: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setUploadedFile: React.Dispatch<React.SetStateAction<IUploadResponse | undefined>>;
    setIsYoutubeCalloutVisible: React.Dispatch<React.SetStateAction<boolean>>;
    setTextYoutubeInput: React.Dispatch<React.SetStateAction<string>>;
    setIsYoutubeLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setActiveComp: React.Dispatch<React.SetStateAction<gactivecomp>>;
    setCurrentP: React.Dispatch<React.SetStateAction<number>>;
    setSourceHighlighting: React.Dispatch<React.SetStateAction<boolean>>;
    setDocAudio: React.Dispatch<React.SetStateAction<boolean>>;
}>({
    user: emptyuser(),
    loggedInUser: emptyloggedinuser(),
    permissionLevels: {},
    category: '',
    fileList: [],
    groupList: [],
    selectedGroup: emptygroup(),
    compUsrs: [],
    allUsrs: [],
    compMbrshps: [],
    isCalloutVisible: false,
    selectedFiles: [],
    isLoading: false,
    uploadedFile: undefined,
    isYoutubeCalloutVisible: false,
    textYoutubeInput: '',
    isYoutubeLoading: false,
    activeComp: emptycompany(),
    currentP: 0,
    sourceHighlighting: true,
    docAudio: true,
    setUser: () => {},
    setLoggedInUser: () => {},
    setPermissionLevels: () => {},
    setCategory: () => {},
    setFileList: () => {},
    setGroupList: () => {},
    setSelectedGroup: () => {},
    setCompUsrs: () => {},
    setAllUsrs: () => {},
    setCompMbrshps: () => {},
    setIsCalloutVisible: () => {},
    setSelectedFiles: () => {},
    setIsLoading: () => {},
    setUploadedFile: () => {},
    setIsYoutubeCalloutVisible: () => {},
    setTextYoutubeInput: () => {},
    setIsYoutubeLoading: () => {},
    setActiveComp: () => {},
    setCurrentP: () => {},
    setSourceHighlighting: () => {},
    setDocAudio: () => {}
});

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const GlobalProvider: React.FunctionComponent<PropsWithChildren<{}>> = ({ children }) => { //
    const [user, setUser] = useState<guser>(emptyuser());
    const [loggedInUser, setLoggedInUser] = useState<gloggedinuser>(emptyloggedinuser());
    const [permissionLevels, setPermissionLevels] = useState<{ [key: number]: string }>(permissionlevels());
    const [category, setCategory] = useState<string>('');
    const [fileList, setFileList] = useState<Array<gfile>>([]);
    const [groupList, setGroupList] = useState<Array<ggroup>>([]);
    const [selectedGroup, setSelectedGroup] = useState<gselectedgroup>(emptygroup());
    const [compUsrs, setCompUsrs] = useState<Array<gcompusrs>>([]);
    const [allUsrs, setAllUsrs] = useState<Array<gallusrs>>([]);
    const [compMbrshps, setCompMbrshps] = useState<Array<gcompmbrshps>>([]);
    const [isCalloutVisible, setIsCalloutVisible] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [uploadedFile, setUploadedFile] = useState<IUploadResponse>();
    const [isYoutubeCalloutVisible, setIsYoutubeCalloutVisible] = useState<boolean>(false);
    const [textYoutubeInput, setTextYoutubeInput] = useState<string>('');
    const [isYoutubeLoading, setIsYoutubeLoading] = useState<boolean>(false);
    const [activeComp, setActiveComp] = useState<gactivecomp>(emptycompany());
    const [currentP, setCurrentP] = useState<number>(0);
    const [sourceHighlighting, setSourceHighlighting] = useState<boolean>(true);
    const [docAudio, setDocAudio] = useState<boolean>(true);
    const value = {
        user,
        loggedInUser,
        permissionLevels,
        category,
        fileList,
        groupList,
        selectedGroup,
        compUsrs,
        allUsrs,
        compMbrshps,
        isCalloutVisible,
        selectedFiles,
        isLoading,
        uploadedFile,
        isYoutubeCalloutVisible,
        textYoutubeInput,
        isYoutubeLoading,
        activeComp,
        currentP,
        sourceHighlighting,
        docAudio,
        setUser,
        setLoggedInUser,
        setPermissionLevels,
        setCategory,
        setFileList,
        setGroupList,
        setSelectedGroup,
        setCompUsrs,
        setAllUsrs,
        setCompMbrshps,
        setIsCalloutVisible,
        setSelectedFiles,
        setIsLoading,
        setUploadedFile,
        setIsYoutubeCalloutVisible,
        setTextYoutubeInput,
        setIsYoutubeLoading,
        setActiveComp,
        setCurrentP,
        setSourceHighlighting,
        setDocAudio
    };

    return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};
